import React from 'react';
import { Box, LinearProgress, Typography, Stack, Alert, AlertTitle } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { computeJobseekerProfileCompletion } from '../../utils/profileCompletion';

const LABELS = {
    first_name: 'First name',
    last_name: 'Last name',
    home_address: 'Street address',
    postalcode: 'Postal code',
    city: 'City',
    country: 'Country',
    phone_number: 'Phone',
    about: 'About me',
    skills: 'At least one skill',
    education: 'At least one education entry',
    languages: 'At least one language',
};

function ProfileCompletionWidget({ profile, skills = [], educations = [], languages = [], onDismiss }) {
    const { percent, checks } = computeJobseekerProfileCompletion(profile, {
        skills,
        educations,
        languages,
    });

    const missing = checks.filter((c) => !c.done);
    const complete = missing.length === 0;

    return (
        <Alert
            severity={complete ? 'success' : 'info'}
            variant="outlined"
            onClose={onDismiss}
            sx={{
                alignItems: 'flex-start',
                '& .MuiAlert-message': { width: '100%' },
            }}
        >
            <AlertTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, pr: 0 }}>
                <span>Profile completion</span>
                <Typography component="span" variant="h6" fontWeight={700} color="inherit" sx={{ opacity: 0.95 }}>
                    {percent}%
                </Typography>
            </AlertTitle>
            <Box sx={{ width: '100%' }}>
                <LinearProgress
                    variant="determinate"
                    value={percent}
                    color={complete ? 'success' : 'primary'}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        mb: 1.5,
                        mt: 0.5,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': { borderRadius: 4 },
                    }}
                />
               
                {missing.length > 0 && (
                    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Still to add
                        </Typography>
                        {missing.slice(0, 6).map((c) => (
                            <Stack direction="row" spacing={1} alignItems="center" key={c.key}>
                                <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {LABELS[c.key] || c.key}
                                </Typography>
                            </Stack>
                        ))}
                        {missing.length > 6 && (
                            <Typography variant="caption" color="text.disabled">
                                +{missing.length - 6} more
                            </Typography>
                        )}
                    </Stack>
                )}
                {complete && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'success.dark' }}>
                            Core profile is complete
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Alert>
    );
}

export default ProfileCompletionWidget;
